import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Drawer, Popconfirm, Row, Space, Tree, Typography, Tooltip, Tag } from 'antd';
// import { useNavigate } from 'react-router-dom';
import { openNotification } from '../../../utils';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';

import { listReportNodesAll, deleteReportNode, updateReportNode } from '../services/report-nodeService';
import ReportNodeForm from './Form';
import QuestionList from '../../questions/views/List';

interface ReportNodeListProps {
  referenceId?: number;
  reportFormatPublicUuid?: string;
  reportAttributeId?: number;
}

const measuringInstrumentFieldLabelMap: Record<string, string> = {
  INVENTORY_NO: 'ID = Envanter No',
  INSTRUMENT_NAME: 'Ölçüm Cihazı Adı',
  SERIAL_NUMBER: 'Seri Numarası',
  BRAND: 'Marka',
  MODEL: 'Model',
  CALIBRATION_NUMBER: 'Kalibrasyon Numarası',
  CALIBRATION_DATE: 'Kalibrasyon Tarihi',
  CALIBRATION_VALID_UNTIL: 'Kalibrasyon Geçerlilik Tarihi',
  CALIBRATION_ORGANIZATION: 'Kalibrasyon Kuruluşu',
};

export default function ReportNodeList({ referenceId, reportFormatPublicUuid, reportAttributeId }: ReportNodeListProps) {
  const [nodes, setNodes] = useState<any[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [parentId, setParentId] = useState<number | undefined>();
  const [selectedId, setSelectedId] = useState<number | undefined>();
  // const navigate = useNavigate();
  

  const fetchData = async () => {
    if (referenceId !== undefined) {
      const res = await listReportNodesAll('reportFormatId.equals=' + referenceId);
      setNodes(res);
      setTreeData(buildTree(res));
    }
  };
  useEffect(() => { fetchData(); }, [referenceId]);

  const findNode = (id: number) => nodes.find(n => n.id === id);
  const selectedNode = useMemo(() => (selectedId ? findNode(selectedId) : undefined), [selectedId, nodes]);
  const selectedNodeType = (selectedNode?.type ?? '').toUpperCase();
  const getParentId = (node: any) => {
    const p = node?.parent;
    return typeof p === 'object' ? p?.id : p;
  };

  const handleDrop = async (info: any) => {
    const dragId = Number(info.dragNode.key);
    const dropId = Number(info.node.key);
    const dragNode = findNode(dragId);
    const dropNode = findNode(dropId);
    if (!dragNode || !dropNode) return;

    const sortSiblings = (items: any[]) => {
      return items.sort((a, b) => {
        const aOrder = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : 0;
        const bOrder = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        const aTitle = String(a.title ?? '');
        const bTitle = String(b.title ?? '');
        return aTitle.localeCompare(bTitle, 'tr', { sensitivity: 'base' });
      });
    };

    const normalizeSiblings = (parentId: number | undefined) => {
      const filtered = nodes
        .filter(n => getParentId(n) === parentId && n.id !== dragId)
        .map(n => ({ ...n, parent: getParentId(n) }));
      return sortSiblings(filtered);
    };

    const reorderWithinParent = (parentId: number | undefined) => {
      const ordered = sortSiblings(
        nodes
          .filter(n => getParentId(n) === parentId && n.id !== dragId)
          .map(n => ({ ...n, parent: getParentId(n) }))
      );
      const dropPosition = Number.isFinite(info.dropPosition) ? info.dropPosition : 0;
      const targetIndex = ordered.findIndex(n => n.id === dropId);
      let insertIndex: number;
      if (targetIndex === -1) {
        insertIndex = dropPosition <= 0 ? 0 : ordered.length;
      } else {
        insertIndex = dropPosition <= 0 ? targetIndex : targetIndex + 1;
      }
      const result = [...ordered];
      result.splice(insertIndex, 0, { ...dragNode, parent: parentId });
      return { list: result, insertIndex };
    };

    let newParentId: number | undefined;
    let siblings: any[] = [];
    if (!info.dropToGap) {
      newParentId = dropId;
      siblings = normalizeSiblings(newParentId);
      siblings.push({ ...dragNode, parent: newParentId });
    } else {
      newParentId = getParentId(dropNode);
      const { list } = reorderWithinParent(newParentId);
      siblings = list;
    }
    try {
      await Promise.all(
        siblings.map((n, index) =>
          updateReportNode(n.id, {
            title: n.title,
            code: n.code,
            description: n.description,
            sortOrder: index,
            type: n.type ?? 'STANDARD',
            showInReportForm: n.showInReportForm ?? true,
            showInReport: n.showInReport ?? true,
            measuringInstruments:
              (Array.isArray(n.measuringInstruments) ? n.measuringInstruments : [])
                .map((mi: any) => (typeof mi === 'object' ? mi?.id : mi))
                .filter((mi: any) => mi !== undefined && mi !== null),
            controlAreas:
              (Array.isArray(n.controlAreas) ? n.controlAreas : [])
                .map((controlArea: any) => (typeof controlArea === 'object' ? controlArea?.id : controlArea))
                .filter((controlArea: any) => controlArea !== undefined && controlArea !== null),
            measuringInstrumentFields:
              Array.isArray(n.measuringInstrumentFields) ? n.measuringInstrumentFields : undefined,
            parent: n.parent,
            reportFormat: typeof n.reportFormat === 'object' ? n.reportFormat.id : n.reportFormat,
          })
        )
      );
      openNotification('success', 'Başarılı', 'Sıralama Güncellendi');
      setSelectedId(dragId);
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Sıralama güncellenemedi');
    }
  };
  const openCreate = (pId?: number) => { setEditingId(undefined); setParentId(pId); setOpen(true); };
  const openEdit = (id: number) => { setEditingId(id); setParentId(undefined); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleDelete = async (id: number) => {
    try {
      await deleteReportNode(id);
      openNotification('success', 'Başarılı', 'Bölüm Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Bölüm silme işlemi başarısız');
    }
  };
  const buildTree = (nodes: any[]) => {
    const map = new Map<number, any>();
    const roots: any[] = [];
    nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    nodes.forEach(n => {
      map.set(n.id, { key: n.id, title: renderTitle(n), children: [] });
    });
    nodes.forEach(n => {
      const parent = typeof n.parent === 'object' ? n.parent?.id : n.parent;
      const node = map.get(n.id);
      if (parent && map.has(parent)) {
        map.get(parent).children.push(node);
      } else {
        roots.push(node);
      }
    });
    return roots;
  };

  const renderTitle = (node: any) => (
    <span>
      {node.title}
      <Space size={4} style={{ marginLeft: 8 }}>
        <Button size='small' type='link' onClick={(e) => { e.stopPropagation(); openCreate(node.id); }}>Ekle</Button>
        <Tooltip title='Düzenle'>
          <Button
            type='text'
            size='small'
            icon={<img src={PenIcon} alt="pen" className="icon-sm" />}
            shape="circle"
            onClick={(e) => { e.stopPropagation(); openEdit(node.id); }}
            className="icon-button-plain-sm"
          />
        </Tooltip>
        <Popconfirm title='Sil?' onConfirm={() => handleDelete(node.id)}>
          <Tooltip title='Sil'>
            <Button
              type='text'
              size='small'
              icon={<img src={TrashIcon} alt="trash" className="icon-sm" />}
              shape="circle"
              onClick={(e) => e.stopPropagation()}
              className="icon-button-plain-sm ml-12px"
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    </span>
  );
  return (
    <div>
      
      <Row gutter={16}>
        <Col span={8}>
          <Card
            title="Rapor Yapısı"
            extra={
              <Space>
                <Button
                  onClick={() => {
                    const reportFormatToken = reportFormatPublicUuid || referenceId;
                    if (reportFormatToken) {
                      const url = window.location.origin + '/report-nodes/view-all/' + reportFormatToken;
                      window.open(url, '_blank', 'noopener');
                    }
                  }}
                  disabled={!reportFormatPublicUuid && !referenceId}
                >
                  Görüntüle
                </Button>
                <Button type='primary' onClick={() => openCreate()}>Ekle</Button>
              </Space>
            }
          >
            <Tree
              treeData={treeData}
              defaultExpandAll
              onSelect={(keys) => setSelectedId(keys.length ? Number(keys[0]) : undefined)}
              selectedKeys={selectedId ? [selectedId] : []}
              draggable
              onDrop={handleDrop}
            />
          </Card>
        </Col>
        <Col span={16}>
            {selectedNode ? (
              (selectedNodeType === 'PANEL_CONTROL' || selectedNodeType === 'STANDARD' || selectedNodeType === 'APPROVAL' || selectedNodeType === 'CONTROL_CRITERIA_AND_TESTS') ? (
                <QuestionList
                  referenceId={selectedId}
                  reportFormatId={referenceId}
                  nodeTitle={selectedNode?.title}
                  reportAttributeId={reportAttributeId}
                />
              ) : selectedNodeType === 'MEASURING_INSTRUMENT' ? (
                <Card title='Ölçüm Cihazları' extra={<Button type='link' onClick={() => selectedNode?.id && openEdit(selectedNode.id)}>Düzenle</Button>}>
                  
                      {Array.isArray(selectedNode.measuringInstruments) && selectedNode.measuringInstruments.length > 0 ? (
                    <Row >
                      {selectedNode.measuringInstruments.map((mi: any) => (
                        <Tag color="blue" key={typeof mi === 'object' ? mi?.id : mi}>
                          {typeof mi === 'object' ? (mi?.name ?? mi?.id) : mi}
                        </Tag>
                      ))}
                    </Row>
                  ) : (
                    <Typography.Text type='secondary'>Bu bölüm için henüz ölçü cihazı seçilmedi.</Typography.Text>
                  )}

                  {Array.isArray(selectedNode.measuringInstrumentFields) && selectedNode.measuringInstrumentFields.length > 0 ? (
                    <Row >
                      <Col span={24}>
                      <h5>Ölçüm Cihazı Tablo Bilgileri</h5>
                      {selectedNode.measuringInstrumentFields.map((fieldKey: string) => (
                        <Tag key={fieldKey} className='ml-2'>
                          {measuringInstrumentFieldLabelMap[fieldKey] ?? fieldKey}
                        </Tag>
                      ))}
                      </Col>
                    </Row>
                  ) : null}
              
                </Card>
              ) : (
                <Typography.Text>Bu bölüm tipi için soru eklenemez.</Typography.Text>
              )
            ) : (
              <Typography.Text>Lütfen bir bölüm seçiniz</Typography.Text>
            )}
        </Col>
      </Row>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Bölüm Düzenle' : 'Bölüm Oluştur'}>
        <ReportNodeForm id={editingId} parentId={parentId} onSuccess={() => { handleClose(); fetchData(); }} referenceId={referenceId} />
      </Drawer>
    </div>
  );
}
